import { DataProviderBase, UIProviderBase } from '../../semantic';
import { AdmsAsset } from '../../redux';
import { subsetByLangOnly } from '../../virtuoso/helper'; 
import { Concepts, ConceptSchemes, Concept, Foaf, vCard, Language, 
  ExternalLink, ExternalLinkOrHTML, AssetLink, HasUnit,
  DefaultValue, FileDownload, DescriptionValue, DCTermsIdentifierLink, License,
  KeywordValue, InScheme, LinkConcept, LinkSimple} from './AssetWidgets';
import { DefaultEditor, URLEditor, DateEditor, NumberPositiveEditor, ByteSizeEditor, 
  DescriptionEditor, SwitchEditor, SelectUriEditor} from './AssetInputs';
import AdmsOntologyHelper from './AdmsOntologyHelper';
import LiteralSimpleEditor from '../editor/LiteralSimpleEditor';
import ThemeTaxonomyEditor from '../editor/ThemeTaxonomyEditor';
import ObjectPropertyEditor from '../editor/ObjectPropertyEditor';
import AnnotationPropertyEditor from '../editor/AnnotationPropertyEditor';
import LinkSimpleEditor from '../editor/LinkSimpleEditor';
import LinkAssetEditor from '../editor/LinkAssetEditor';

const ontology = new AdmsOntologyHelper();

/** Имя графа каталога по умолчанию */
const default_graph ='http://csi.semanticpro.org/ADMS';

/** Описание данных ADMS и их получение из графовой БД. 
 * Определяет доступные типы */
export class AdmsDataProvider extends DataProviderBase {
  
  constructor(graph = default_graph) { 
    super(graph); 
  };


  /** Обновляет описание онтологии каталог  */
  updateOntology(content){
    ontology.parseSKSAOntology(content);
  };

  /** Получить значение метки для IRI, полученного из triplet */
  getLabelByIRI = (IRI, lang=undefined, triplet=undefined) => dispatch =>{
    //console.log("getLabelByIRI", AdmsAsset);
    return dispatch(AdmsAsset.loadLabelByIRI(IRI))
      .then(response =>{
        const { head ,results } = response;
        const { bindings } = results;
        const labels = subsetByLangOnly(bindings, lang, "label");
        const label = (labels.length>0)? labels[0].label.value:(bindings && Array.isArray(bindings) && (bindings.length>0))?bindings[0].label.value:IRI;
        //console.log("getLabelByIRI!!!!", labels, label, bindings);
        return Promise.resolve({IRI, label: label, lang: lang});
      })
      .catch(e => { 
        return Promise.resolve({IRI, label: IRI, lang: lang, e});
      });
  }

  /** Получить значение метки для IRI, полученного из triplet */
  getLabelOrTitleByIRI = (IRI, lang=undefined) => dispatch =>{
    return dispatch(AdmsAsset.loadLabels(IRI))
      .then(response =>{
        const { head , labels } = response;
        let allLabels = [];
        //Метки без языковых констант
        const notLangLabels = subsetByLangOnly(labels, undefined, "object");
        const langLables = subsetByLangOnly(labels, lang, "object");
        allLabels = [...langLables, ...labels]
        const label = (allLabels.length>0)? allLabels[0].object.value:IRI;
        //Значение, которое задано без языковой константы
        const noLangLabel = (notLangLabels.length>0)?notLangLabels[0].object.value:IRI;
        //Пример ответа
        //{IRI: 'http://csi.semanticpro.org/vcard/2023#Ivanov', label: 'http://csi.semanticpro.org/vcard/2023#Ivanov', lang: 'ru', noLangLabel: 'Иванов И.И.'}
        return Promise.resolve({IRI, label: label, lang: lang, noLangLabel});    
      })
      .catch(e => { 
        return Promise.resolve({IRI, label: IRI, lang: lang, e});
      });
  }

  /** Если  rdfType равен undefined, то возвращается список всех свойств */
  getProperties(rdfType, purpose=undefined) {
    return ontology.getProperties(rdfType, purpose);
  }

  /** Возвращает true, если предикат определяет объетное свойства (т.е. ссылка) */
  isObjectProperty(predicat){
    return ontology.isObjectProperty(predicat);
  }

  /** Возвращает массив типов назначенных для свойства 
   * (см. значение для предиката http://www.w3.org/2000/01/rdf-schema#:range */
  getRanges(iriProperty){
    return ontology.getRanges(iriProperty);
  }

  getSubClasses(classURI, recursive = true) {
    return ontology.getSubClasses(classURI, recursive)
  }

  getSuperClasses(classURI, recursive = true) {
    return ontology.getSuperClasses(classURI, recursive);
  }

  getLabel(iri, lang = undefined) {
    return ontology.getLabel(iri, lang);
  }
}


/** Провайдер, для получения визуальных компонент, их параметров, 
 * состава элементов визуализации. Например свойства, которые отображаются,
 * который можно редактирования, переводить и т.п.
 */
export class AdmsUIProvider extends UIProviderBase {
  
  constructor(graph = default_graph) { 
    super(graph); 
  };

  /** Получение компоненты для отображения в представлениях. 
   * Обычно в вызывается в компонентах отобаржения карточки каталога, карточки распространения и т.п.
   * predicat - URI предиката, который содержит данные для просмотра/редактирования
   * operation - вид операции. Может быть "view" или "edit"
   * type - тип элемента (класс), для которого должен быть добавлен элемент. 
   *  Может задаваться значением объекта у триплета с предикатом http://www.w3.org/1999/02/22-rdf-syntax-ns#type */
  getUIComponent = (predicat, operation="view", type=null) => { 
    //console.log("Providers.js getUIComponent", predicat, operation, type, ontology);
    if (operation=="edit"){
      if ("http://www.w3.org/ns/dcat#themeTaxonomy" == predicat) 
        return ThemeTaxonomyEditor;
      if (["http://www.w3.org/ns/adms#supportedSchema", "http://www.w3.org/ns/dcat#accessURL",
          "http://www.w3.org/ns/dcat#downloadURL", "http://www.w3.org/ns/dcat#byteSize",
          "http://purl.semanticpro.org/ontologies/sksa/wiki"].includes(predicat))
        return LiteralSimpleEditor;
      if (["http://purl.org/dc/terms/publisher", "http://purl.org/dc/terms/license",
        "http://purl.org/dc/terms/replaces"
      ].includes(predicat))
        return ObjectPropertyEditor;
      if (["http://purl.org/dc/terms/title", "http://www.w3.org/2000/01/rdf-schema#label",
        "http://www.w3.org/2004/02/skos/core#definition"].includes(predicat))
        return AnnotationPropertyEditor;
      if (["http://www.w3.org/ns/dcat#keyword", "http://purl.org/dc/terms/temporal", 
        "http://purl.org/dc/terms/spatial", "http://schema.org/variableMeasured",
        "http://purl.org/vocab/vann/preferredNamespaceUri", "http://purl.org/vocab/vann/preferredNamespacePrefix"
      ].includes(predicat))
        return LiteralSimpleEditor;
      if (["http://www.w3.org/1999/xhtml/vocab#last", "http://www.w3.org/ns/adms#next",
          "http://www.w3.org/ns/adms#prev", "http://www.w3.org/ns/adms#sample", "http://www.w3.org/ns/adms#translation",
          "http://csi.semanticpro.org/2016/ns#disjunction", "http://csi.semanticpro.org/2016/ns#equivalent",
          "http://csi.semanticpro.org/2016/ns#extend", "http://csi.semanticpro.org/2016/ns#generalize",
          "http://csi.semanticpro.org/2016/ns#import","http://csi.semanticpro.org/2016/ns#metadata",
          "http://csi.semanticpro.org/2016/ns#specialize", "http://csi.semanticpro.org/2016/ns#domain",
          ].includes(predicat)) return LinkAssetEditor;
      if (["http://www.w3.org/2004/02/skos/core#hasTopConcept"]
        .includes(predicat)) return Concepts;
      
      if (ontology.isAnnotationProperty(predicat))
        return AnnotationPropertyEditor;
      if (ontology.isLiteralProperty(predicat))
        return LiteralSimpleEditor;      
      if (ontology.isObjectProperty(predicat))
        return LinkSimpleEditor;      
    }
    if (["http://www.w3.org/ns/dcat#contactPoint", 
      "http://purl.org/dc/terms/contributor"].includes(predicat)) 
      return vCard;
    if (["http://purl.org/dc/terms/creator","http://purl.org/dc/terms/publisher"].includes(predicat)) 
      return Foaf;
    if ("http://purl.org/dc/terms/language" == predicat) 
      return Language;
    if (["http://www.w3.org/ns/dcat#landingPage", "http://www.w3.org/2007/05/powder-s#describedby",
      "http://www.w3.org/ns/dcat#accessURL", "http://xmlns.com/foaf/0.1/homepage", 
      "http://creativecommons.org/ns#legalcode"].includes(predicat)) 
      return ExternalLinkOrHTML; 

    if ("http://www.w3.org/2004/02/skos/core#closeMatch" == predicat) 
      return ExternalLink;

    if (["http://www.w3.org/ns/dcat#theme", "http://www.w3.org/2004/02/skos/core#hasTopConcept"]
      .includes(predicat)) return Concepts;
    if ("http://www.w3.org/ns/dcat#themeTaxonomy" == predicat) return ConceptSchemes;
    if ("http://csi.semanticpro.org/adms/wf/maturityLevel" == predicat) return Concept;
    if (["http://www.w3.org/1999/xhtml/vocab#last", "http://www.w3.org/ns/adms#next",
         "http://www.w3.org/ns/adms#prev", "http://www.w3.org/ns/adms#sample", "http://www.w3.org/ns/adms#translation",
         "http://csi.semanticpro.org/2016/ns#disjunction", "http://csi.semanticpro.org/2016/ns#equivalent",
         "http://csi.semanticpro.org/2016/ns#extend", "http://csi.semanticpro.org/2016/ns#generalize",
         "http://csi.semanticpro.org/2016/ns#import","http://csi.semanticpro.org/2016/ns#metadata",
         "http://csi.semanticpro.org/2016/ns#specialize", "http://csi.semanticpro.org/2016/ns#domain",
        ].includes(predicat)) return AssetLink;
    if (["http://www.w3.org/ns/dcat#downloadURL"].includes(predicat))
      return FileDownload;
    if ("http://purl.org/dc/terms/identifier" == predicat) 
      return DCTermsIdentifierLink;      
    if ("http://purl.org/dc/terms/license" == predicat) 
      return License;
    if ("http://www.w3.org/ns/org#hasUnit" == predicat) 
      return HasUnit;
    if (["http://purl.org/dc/terms/description", "http://www.w3.org/2000/01/rdf-schema#comment"].includes(predicat)) 
      return DescriptionValue;

    if ("http://www.w3.org/ns/dcat#keyword" == predicat) 
      return KeywordValue;

    if (["http://www.w3.org/2004/02/skos/core#inScheme"]
      .includes(predicat)) return InScheme;

    if (["http://www.w3.org/2004/02/skos/core#broader", "http://www.w3.org/2004/02/skos/core#related"]
      .includes(predicat)) return LinkConcept;

      if (ontology.isObjectProperty(predicat))
        return LinkSimple;
    return DefaultValue;
  };

  getFormItem(predicat, type=null) {
    if (["http://www.w3.org/ns/dcat#accessURL", "http://www.w3.org/ns/dcat#downloadURL",
      "http://purl.semanticpro.org/ontologies/sksa/wiki"].includes(predicat)) 
      return URLEditor;
    if ("http://www.w3.org/ns/dcat#byteSize" == predicat) 
      return ByteSizeEditor;
    if ("http://purl.org/dc/terms/issued" == predicat) 
      return DateEditor;
    if (["http://purl.org/dc/terms/description","http://www.w3.org/2004/02/skos/core#definition"]
       .includes(predicat))
      return DescriptionEditor;
    if ("new.flag.boolean" == predicat)
      return SwitchEditor;
    if ("new.select.uri" == predicat)
      return SelectUriEditor;
    return DefaultEditor;
  }
}

/** Фабрика  */
class ProvidersFactory {
  constructor(){
      this._graph = default_graph;
      this._ui = new AdmsUIProvider();
      this._provider = new AdmsDataProvider();
  }

  get graph () { return this._graph; };
  set graph (graph) { this._graph = graph; this._provider = new AdmsDataProvider(graph); };

  get provider () { return this._provider; };
  set provider (provider) { this._provider = provider; };

  get ui () { return this._ui; };
  set ui (ui) { this._ui = ui; };
}

export const factory = new ProvidersFactory();